import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { setOtaRolloutStatus, updateOtaRelease } from "../api/otaAdminApi";
import SearchPicker from "./SearchPicker";

const fieldStyle = { display: "grid", gap: 4 };
const errorText = (error, fallback) => {
  const detail = error.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
};

/** Edit a published release in place: no new upload, the file and its hash stay. */
export function OtaReleaseEditForm({ accessToken, release, onDone, onCancel }) {
  const { t } = useTranslation();
  const [fromVersion, setFromVersion] = useState(release.from_version);
  const [channel, setChannel] = useState(release.channel);
  const [notes, setNotes] = useState(release.release_notes || "");
  const [busy, setBusy] = useState(false);

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await updateOtaRelease(accessToken, release.id, {
        from_version: fromVersion.trim() || "*",
        channel,
        release_notes: notes,
      });
      toast.success(t("admin.ota.edit.releaseUpdated", { version: release.version }));
      onDone();
    } catch (error) {
      toast.error(errorText(error, t("admin.ota.edit.releaseFailed")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} style={{ display: "grid", gap: 10, maxWidth: 560 }}>
      <label style={fieldStyle}>
        {t("admin.ota.edit.fromVersion")}
        <input value={fromVersion} onChange={(e) => setFromVersion(e.target.value)} />
      </label>
      <label style={fieldStyle}>
        {t("admin.ota.edit.channel")}
        <select value={channel} onChange={(e) => setChannel(e.target.value)}>
          <option value="dev">dev</option>
          <option value="canary">canary</option>
          <option value="stable">stable</option>
        </select>
      </label>
      <label style={fieldStyle}>
        {t("admin.ota.edit.notes")}
        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" disabled={busy}>{t("admin.ota.edit.save")}</button>
        <button type="button" onClick={onCancel}>{t("admin.ota.edit.cancel")}</button>
      </div>
    </form>
  );
}

/** "10, 50,100" -> [10,50,100]; empty -> undefined. */
export const parseRamp = (text) => {
  const parts = String(text || "").split(/[\s,]+/).filter(Boolean);
  return parts.length ? parts.map(Number) : undefined;
};

/** Move a rollout to another target (global / tenant / device) and/or change its percentage. */
export function OtaRolloutEditForm({ accessToken, rollout, searchTenants, searchEdges, onDone, onCancel }) {
  const { t } = useTranslation();
  const [tenant, setTenant] = useState(
    rollout.tenant_id && !rollout.edge_id ? { id: rollout.tenant_id, label: rollout.tenant_name || rollout.tenant_id } : null,
  );
  const [edge, setEdge] = useState(
    rollout.edge_id ? { id: rollout.edge_id, label: rollout.edge_name || rollout.edge_id } : null,
  );
  const [percentage, setPercentage] = useState(rollout.rollout_percentage);
  const [ramp, setRamp] = useState((rollout.ramp_steps || []).join(","));
  const [soak, setSoak] = useState(rollout.soak_minutes ?? 30);
  const [busy, setBusy] = useState(false);

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const scope = edge ? "edge" : tenant ? "tenant" : "global";
      await setOtaRolloutStatus(accessToken, rollout.id, {
        scope,
        tenant_id: scope === "tenant" ? tenant.id : undefined,
        edge_id: scope === "edge" ? edge.id : undefined,
        // a hand-set percentage / new ramp restarts the soak clock, so only send what changed
        rollout_percentage: Number(percentage) === rollout.rollout_percentage ? undefined : Number(percentage),
        ramp_steps: ramp === (rollout.ramp_steps || []).join(",") ? undefined : parseRamp(ramp),
        soak_minutes: Number(soak) === rollout.soak_minutes ? undefined : Number(soak),
      });
      toast.success(t("admin.ota.edit.rolloutUpdated"));
      onDone();
    } catch (error) {
      toast.error(errorText(error, t("admin.ota.edit.rolloutFailed")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} style={{ display: "grid", gap: 10, maxWidth: 560 }}>
      <SearchPicker
        label={t("admin.ota.edit.tenant")}
        placeholder={t("admin.ota.edit.tenantSearch")}
        value={tenant}
        onChange={(picked) => { setTenant(picked); setEdge(null); }}
        fetchItems={searchTenants}
      />
      <SearchPicker
        label={t("admin.ota.edit.device")}
        placeholder={t("admin.ota.edit.deviceSearch")}
        value={edge}
        onChange={setEdge}
        fetchItems={searchEdges(tenant?.id)}
        deps={[tenant?.id]}
      />
      <label style={fieldStyle}>
        {t("admin.ota.edit.canaryPercent")}
        <input type="number" min="0" max="100" value={percentage} onChange={(e) => setPercentage(e.target.value)} />
      </label>
      <label style={fieldStyle}>
        {t("admin.ota.edit.rampSteps")}
        <input value={ramp} placeholder="10,50,100" onChange={(e) => setRamp(e.target.value)} />
      </label>
      <label style={fieldStyle}>
        {t("admin.ota.edit.soakMinutes")}
        <input type="number" min="1" value={soak} onChange={(e) => setSoak(e.target.value)} />
      </label>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" disabled={busy}>{t("admin.ota.edit.save")}</button>
        <button type="button" onClick={onCancel}>{t("admin.ota.edit.cancel")}</button>
      </div>
    </form>
  );
}
